// Fabric (toile) reference detection and photo resolution, shared by table, popup, print and email import.

const ENTITY = /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi;
const entityCache = new Map();

export function decodeEntities(value) {
  const s = String(value ?? "");
  if (!s.includes("&")) return s;
  return s.replace(ENTITY, (m, body) => {
    if (body[0] === "#") {
      const cp = body[1].toLowerCase() === "x" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      try { return String.fromCodePoint(cp); } catch { return m; }
    }
    if (!entityCache.has(m)) entityCache.set(m, new DOMParser().parseFromString(m, "text/html").body.textContent || m);
    return entityCache.get(m);
  });
}

// Must stay identical to normalize_part() in backend fabric_images.py
export const normalizeFabricPart = (value) => decodeEntities(value)
  .replace(/[™®©]/g, "").toUpperCase().replace(/\s+/g, " ").trim().replace(/[.,;:]+$/, "").trim();

// Collection equivalences from Admin (alias -> library collection), kept in sync by useFabricLibrary.
let collectionAliases = new Map();
export const setCollectionAliases = (map) => { collectionAliases = map; };
export const canonicalCollection = (normalized) => collectionAliases.get(normalized) || normalized;
const aliasesOf = (collection) => [...collectionAliases].filter(([, c]) => c === collection).map(([a]) => a);

export const makeFabricKey = (collection, code) => `${canonicalCollection(normalizeFabricPart(collection))}|${normalizeFabricPart(code)}`;

export function canonicalFabricKey(key) {
  const sep = String(key || "").lastIndexOf("|");
  return sep >= 0 ? `${canonicalCollection(key.slice(0, sep))}|${key.slice(sep + 1)}` : key || "";
}

const fabricLabel = (collection, code) => (collection ? `${collection} · ${code}` : code);

const REF_MARKER = /(?:R[ée]f[ée]ren(?:ce|cia|z)|Riferimento|Farbe\/Design\s+des\s+Stoffs)\s*:\s*/gi;
const NOT_FABRIC = /^(?:de\s+|du\s+|of\s+|der\s+)?(?:la\s+|votre\s+|your\s+)?(?:commande|order|transaction|paiement|payment|client|colis|suivi|bestellung|facture|invoice)/i;
const COLLECTION_MARKER = /(?:Qualit[ée]\s+de\s+la\s+toile|Qualit[äa]tsmarke|Stoffqualit[äa]t|Fabric\s+quality|Calidad\s+de\s+la\s+lona|Qualit[àa]\s+del\s+tessuto)\s*:\s*/gi;
const SEGMENT_END = /\n|\t|(?:Largeur|Hauteur|Longueur|Quantit[ée]|Breite|H[öo]he|L[äa]nge|Menge|Width|Height|Length|Quantity|Ancho|Alto|Larghezza|Altezza|Finition|Dimensions?|R[ée]f[ée]ren(?:ce|cia|z)|Qualit[ée]\s+de\s+la\s+toile)\s*:/i;
const CODE_TOKEN = /\b(?=[A-Z0-9]*\d)[A-Z0-9]{4,5}\b/gi;

function findCollection(prefix) {
  let found = null;
  for (const c of prefix.matchAll(COLLECTION_MARKER)) found = c;
  if (!found) return null;
  const value = prefix.slice(found.index + found[0].length);
  const stop = value.search(/\.\s|\.$|\n|\t/);
  return (stop >= 0 ? value.slice(0, stop) : value).trim() || null;
}

function guessCollection(segment) {
  const mark = Math.max(segment.lastIndexOf("™"), segment.lastIndexOf("®"));
  if (mark >= 0) return segment.slice(0, mark + 1);
  const firstCode = segment.search(/\b(?=[A-Z0-9]*\d)[A-Z0-9]{4,5}\b/i);
  return firstCode > 0 ? segment.slice(0, firstCode) : "";
}

// "Qualité de la toile: COLLECTION. Reference: COLLECTION 00430 00197 Stan" -> { collection, code: last 4-5 char code }
export function parseFabricRefs(text) {
  const src = String(text ?? "");
  const refs = [];
  let lastEnd = 0;
  for (const m of src.matchAll(REF_MARKER)) {
    if (m.index < lastEnd) continue;
    const segStart = m.index + m[0].length;
    if (NOT_FABRIC.test(src.slice(segStart, segStart + 40))) continue;
    const rest = src.slice(segStart, segStart + 160);
    const stop = rest.search(SEGMENT_END);
    const segment = stop >= 0 ? rest.slice(0, stop) : rest;
    const rawCollection = normalizeFabricPart(findCollection(src.slice(Math.max(lastEnd, m.index - 300), m.index)) ?? guessCollection(segment));
    const collection = canonicalCollection(rawCollection);
    const excluded = new Set(rawCollection.split(" "));
    const codes = [...segment.matchAll(CODE_TOKEN)].filter((c) => !excluded.has(c[0].toUpperCase()));
    if (!codes.length) continue;
    const last = codes[codes.length - 1];
    const code = normalizeFabricPart(last[0]);
    const end = segStart + last.index + last[0].length;
    refs.push({ collection, code, key: `${collection}|${code}`, label: fabricLabel(collection, code), start: m.index, end });
    lastEnd = end;
  }
  return refs;
}

// Fallback for free-form CONTENT: find library codes (and their collection) mentioned in the text.
export function matchLibraryInText(text, library) {
  if (!library?.byCode?.size) return [];
  const norm = normalizeFabricPart(text);
  const refs = [];
  for (const token of new Set([...norm.matchAll(CODE_TOKEN)].map((c) => c[0]))) {
    for (const s of library.byCode.get(token) || []) {
      if (!s.collection || [s.collection, ...aliasesOf(s.collection)].some((c) => norm.includes(c))) {
        refs.push({ collection: s.collection, code: s.code, key: s.key, label: fabricLabel(s.collection, s.code), start: null, end: null });
      }
    }
  }
  return refs;
}

export function groupRefs(refs) {
  const groups = new Map();
  refs.forEach((r) => {
    const g = groups.get(r.key);
    if (g) g.count += 1;
    else groups.set(r.key, { ...r, count: 1 });
  });
  return [...groups.values()];
}

// Fabrics typed by hand on an order: extracted_data._fabrics = "COLLECTION|CODE" keys, one per line.
export const MANUAL_FABRICS_FIELD = "_fabrics";

export function parseManualFabrics(value) {
  return String(value || "").split("\n").map((s) => s.trim()).filter(Boolean).map((key) => {
    const sep = key.lastIndexOf("|");
    const collection = canonicalCollection(sep >= 0 ? key.slice(0, sep) : "");
    const code = sep >= 0 ? key.slice(sep + 1) : key;
    return { collection, code, key: `${collection}|${code}`, label: fabricLabel(collection, code), start: null, end: null, manual: true };
  }).filter((r) => r.code);
}

const photoKey = (p) => canonicalFabricKey((typeof p === "string" ? p : p?.fabric_key) || "");

// photos: print-with-sheet attachments (objects) or their fabric keys (strings); manual: extracted_data._fabrics
export function resolveOrderFabrics(content, library, photos = [], manual = "") {
  const text = String(content ?? "");
  let refs = parseFabricRefs(text);
  if (!refs.length) refs = matchLibraryInText(text, library);
  const detected = new Set(refs.map((r) => r.key));
  const manualRefs = parseManualFabrics(manual).filter((r) => !detected.has(r.key));
  // A code typed by hand replaces the same code detected in the email when that one has no photo.
  const manualCodes = new Set(manualRefs.map((r) => r.code));
  const hasPhoto = (key) => !!library?.byKey?.get(key) || photos.some((p) => photoKey(p) === key);
  const detectedGroups = groupRefs(refs).filter((g) => !(manualCodes.has(g.code) && !hasPhoto(g.key)));
  const grouped = [...detectedGroups, ...groupRefs(manualRefs)];
  const keys = new Set(grouped.map((g) => g.key));
  const fabrics = grouped.map((g) => ({
    ...g,
    orderPhoto: photos.find((p) => photoKey(p) === g.key) || null,
    swatch: library?.byKey?.get(g.key) || null,
  }));
  const generalPhotos = photos.filter((p) => !keys.has(photoKey(p)));
  const missing = fabrics.filter((f) => !f.orderPhoto && !f.swatch);
  let alert = null;
  if (text.trim() || manualRefs.length) {
    if (!fabrics.length && !generalPhotos.length) alert = "Coloris non identifié";
    else if (missing.length && !generalPhotos.length) alert = `Photo de toile manquante : ${missing.map((f) => f.label).join(", ")}`;
  }
  return { refs, fabrics, generalPhotos, missing, alert };
}
